import {
  ClickerGameLayers,
  GameObjectsTags,
  SPACESHIP_ANIMATION_DURATION,
  SPACESHIP_ANIMATION_EASING,
  SPACESHIP_ANIMATION_Y_SHIFT,
  SPACESHIP_FLASHLIGHT_ANIMATION_DELAY,
  SPACESHIP_FLASHLIGHT_ANIMATION_DURATION,
  SPACESHIP_FLASHLIGHT_ANIMATION_EASING,
} from '../../../ClickerGame.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {FULL_BATTERY_LEVEL} from '@/stores/ClickerStateStore/ClickerStateStore.constants';
import {AnimatedSprite, Container, Point, Rectangle, Sprite, Texture} from 'pixi.js';

import anime, {AnimeInstance} from 'animejs';
import {injected} from 'brandi';

import {IGameObject} from '@specials/gcs-engine';

import {PixiViewComponent} from '@/gcs/PixiViewComponent';
import {LocationType} from '@/services/ProjectAuthApi/types';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {GameObjectUtils} from '@/utils/GameObjectUtils';
import {RealRandom} from '@/utils/random/RealRandom';

import {ClickerGameAssetsLoader} from '../../../ClickerGameAssetsLoader';
import {ISpaceshipDataComponent} from './SpaceshipDataComponent';

export class SpaceshipViewComponent extends PixiViewComponent<Container> {
  public kotletka: AnimatedSprite;
  private ship: Sprite;
  private animeSpaceshipInstance: AnimeInstance;
  private animeFlashlightInstance?: AnimeInstance;
  private flashlight: Sprite;
  private dataComponent?: ISpaceshipDataComponent;

  constructor(private readonly assetsLoader: ClickerGameAssetsLoader, private readonly random: RealRandom) {
    super();
  }

  public onAddToGameObject(go: IGameObject): void {
    this.dataComponent = go.getComponentByTag<ISpaceshipDataComponent>(GameObjectsTags.SpaceshipDataComponent);
  }

  public renderView(): Container {
    const spaceshipFull = new Container();

    spaceshipFull.zIndex = ClickerGameLayers.SpaceshipLayer;

    const bundle = this.assetsLoader.bundle;

    this.kotletka = new AnimatedSprite(this.getInitialKotletkaAnimation());
    this.kotletka.anchor.set(0.5);
    this.kotletka.scale.set(0.5);
    this.kotletka.y = -38;

    this.ship = new Sprite(this.getSpaceshipByLocation());
    this.ship.anchor.set(0.5);
    this.ship.scale.set(0.5);

    const currentLocation = this.getLocation();

    if (currentLocation === LocationType.Location3 || currentLocation === LocationType.Location4) {
      this.ship.y -= 13;
    }

    const fire = new AnimatedSprite(GameObjectUtils.createFramesFromSpriteSheet(bundle.fire));
    fire.scale.set(0.5);
    fire.anchor.set(0.5);
    fire.y = 108;
    fire.loop = true;
    fire.play();

    this.flashlight = new Sprite(bundle.starshipFlashlight);
    this.flashlight.scale.set(0.65);
    this.flashlight.anchor.set(0.65);
    this.flashlight.tint = this.getStarTint();
    this.flashlight.angle = 30;
    this.flashlight.alpha = 0;

    spaceshipFull.addChild(fire, this.kotletka, this.ship, this.flashlight);

    spaceshipFull.hitArea = new Rectangle(
      -spaceshipFull.width / 2,
      -spaceshipFull.height / 2 + 60,
      spaceshipFull.width,
      spaceshipFull.height - 120,
    );

    if (this.dataComponent?.isHidden) {
      spaceshipFull.alpha = 0;
    }

    return spaceshipFull;
  }

  public start(): void {
    this.animateSpaceShip();
  }

  public pause(): void {
    this.animeSpaceshipInstance.pause();
    this.animeFlashlightInstance?.pause();
  }

  public resume(): void {
    this.animeSpaceshipInstance.play();
    this.animeFlashlightInstance?.play();
  }

  private getInitialKotletkaAnimation(): Texture[] {
    const bundle = this.assetsLoader.bundle;

    return ClickerStateStore.isLowBatteryLevel(this.dataComponent?.batteryChargeLevel || FULL_BATTERY_LEVEL)
      ? GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaSad)
      : GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaHappy);
  }

  public hide = (): void => {
    this.view.alpha = 0;
  };

  public show = (): void => {
    this.view.alpha = 1;
  };

  private animateSpaceShip(): void {
    this.animeSpaceshipInstance = anime({
      targets: this.ship,
      y: [this.ship.y - SPACESHIP_ANIMATION_Y_SHIFT, this.ship.y + SPACESHIP_ANIMATION_Y_SHIFT],
      duration: SPACESHIP_ANIMATION_DURATION,
      loop: true,
      easing: SPACESHIP_ANIMATION_EASING,
      direction: 'alternate',
    });

    const currentLocation = this.getLocation();

    if (currentLocation === LocationType.Location1 || currentLocation === LocationType.Location2) {
      this.animeFlashlightInstance = anime({
        targets: this.flashlight,
        alpha: [0, 1, 0],
        loop: true,
        easing: SPACESHIP_FLASHLIGHT_ANIMATION_EASING,
        loopBegin: () => {
          this.flashlight.position = this.random.choice([new Point(-71, 70), new Point(3, 75), new Point(78, 70)]);
        },
        duration: SPACESHIP_FLASHLIGHT_ANIMATION_DURATION,
        delay: SPACESHIP_FLASHLIGHT_ANIMATION_DELAY,
      });
    }
  }

  private getSpaceshipByLocation(): Texture {
    const currentLocation = this.getLocation();

    switch (currentLocation) {
      case LocationType.Location1:
        return this.assetsLoader.bundle.spaceshipBronze;

      case LocationType.Location2:
        return this.assetsLoader.bundle.spaceshipSilver;

      case LocationType.Location3:
        return this.assetsLoader.bundle.spaceshipGold;

      case LocationType.Location4:
        return this.assetsLoader.bundle.spaceshipDiamond;
    }
  }

  private getStarTint(): number {
    const currentLocation = this.getLocation();

    switch (currentLocation) {
      case LocationType.Location1:
        return 0xfff171;

      case LocationType.Location2:
        return 0x86ffe9;
    }

    return 0xffffff;
  }

  private getLocation(): LocationType {
    return this.dataComponent?.location || LocationType.Location1;
  }
}

injected(SpaceshipViewComponent, DI_TOKENS.clickerGameAssetsLoader, DI_TOKENS.realRandom);
